import type { AssetSheetColumnMapUpdateInput } from '@atlas/models'
import type { AssetSheetCellFormat, AssetSheetCellFormatPatch, AssetSheetColumnMapEntryDTO } from '@atlas/types'
import { AssetSheetColumnMapScope, AssetSheetLevel, AxisCode } from '@atlas/types'
import { FIELD_LABEL_CLASS, FieldShell, SelectField, type SelectFieldOption } from '@atlas/ui-components'
import { Button, Checkbox, DialogContent, DialogTitle, HelpText, Input, Switch } from '@atlas/ui-primitives'
import { useEffect, useState } from 'react'
import { TokenListField } from './TokenListField'

/** A refusal from the server, with the field it names when it names one. */
export interface ColumnEditError {
	field?: string
	message: string
}

export interface ColumnEditDialogProps {
	/** The column being edited; null closes the dialog. */
	column: AssetSheetColumnMapEntryDTO | null
	/** The other columns of its tab, for the parent column choice. */
	tabColumns: AssetSheetColumnMapEntryDTO[]
	/** How many tabs map a column of this name on this axis, this one included. */
	sameColumnTabs: number
	/** The file types an asset column can take. */
	fileTypes: SelectFieldOption[]
	saving?: boolean
	error?: ColumnEditError | null
	onSave: (input: AssetSheetColumnMapUpdateInput) => void
	onCancel: () => void
}

const LEVELS: SelectFieldOption[] = [
	{ value: AssetSheetLevel.PRODUCT, label: 'Product' },
	{ value: AssetSheetLevel.BRAND, label: 'Brand' },
	{ value: AssetSheetLevel.MARKET, label: 'Market' },
]

const LEVEL_HELP: Record<AssetSheetLevel, string> = {
	[AssetSheetLevel.PRODUCT]: "Files get the row's country, species and product.",
	[AssetSheetLevel.BRAND]: "Files get the row's country, species and brand.",
	[AssetSheetLevel.MARKET]: "Files get the row's country only, like a company logo.",
}

interface Draft {
	columnName: string
	valueCode: string
	level: AssetSheetLevel | ''
	format: AssetSheetCellFormat
	allTabs: boolean
}

const draftOf = (c: AssetSheetColumnMapEntryDTO): Draft => ({
	columnName: c.columnName,
	valueCode: c.value?.code ?? '',
	level: c.level ?? '',
	format: { ...c.cellFormat, split: [...(c.cellFormat.split ?? [])], ignore: [...(c.cellFormat.ignore ?? [])] },
	allTabs: false,
})

/** The reading rules as sent: a rule left off or empty is left out, as the seed writes them. */
function formatOf({ required, rootsOnly, parentColumn, split, ignore }: AssetSheetCellFormat): AssetSheetCellFormat {
	return {
		...(required && { required }),
		...(rootsOnly && { rootsOnly }),
		...(parentColumn && { parentColumn }),
		...(split?.length && { split }),
		...(ignore?.length && { ignore }),
	}
}

const RULES = ['required', 'rootsOnly', 'parentColumn', 'split', 'ignore'] as const

/** The rules that differ from `before`, each with its new value, or null for a rule turned off or emptied. */
function formatPatch(before: AssetSheetCellFormat, after: AssetSheetCellFormat): AssetSheetCellFormatPatch {
	const changed = RULES.filter((rule) => JSON.stringify(before[rule]) !== JSON.stringify(after[rule]))
	return Object.fromEntries(changed.map((rule) => [rule, after[rule] ?? null])) as AssetSheetCellFormatPatch
}

type ColumnFields = Omit<AssetSheetColumnMapUpdateInput, 'scope'>

/**
 * The fields the draft changed from the column, and of its reading rules only the rules changed,
 * so a change to every tab carries only what was edited.
 */
function changedFields(column: AssetSheetColumnMapEntryDTO, draft: Draft): ColumnFields {
	const initial = draftOf(column)
	const columnName = draft.columnName.trim()
	const cellFormat = formatPatch(formatOf(initial.format), formatOf(draft.format))
	const isAsset = column.objectType !== null
	return {
		...(columnName !== initial.columnName && { columnName }),
		...(isAsset && draft.valueCode !== initial.valueCode && { valueCode: draft.valueCode }),
		...(isAsset && draft.level !== initial.level && { level: draft.level || null }),
		...(Object.keys(cellFormat).length > 0 && { cellFormat }),
	}
}

const errorFor = (error: ColumnEditError | null | undefined, field: string) =>
	error?.field?.startsWith(field) ? error.message : undefined

/**
 * Change a mapped column's header, file type, level and reading rules. The change reaches the
 * column's own tab unless "Apply to every tab" is ticked; only the fields edited are sent, so the
 * other tabs keep the rest of their own settings. A refusal is shown next to its field.
 */
export function ColumnEditDialog(props: ColumnEditDialogProps) {
	const { column, tabColumns, sameColumnTabs, fileTypes, saving, error, onSave, onCancel } = props
	const [draft, setDraft] = useState<Draft | null>(null)
	// Reset between openings, so a cancelled draft does not reappear.
	useEffect(() => setDraft(column ? draftOf(column) : null), [column])
	if (!column || !draft) return null

	const isAsset = column.objectType !== null
	const set = (patch: Partial<Draft>) => setDraft({ ...draft, ...patch })
	const changed = changedFields(column, draft)
	const canSave = draft.columnName.trim().length > 0 && Object.keys(changed).length > 0 && !saving

	const submit = (event: React.FormEvent) => {
		event.preventDefault()
		if (!canSave) return
		onSave({
			...changed,
			scope: draft.allTabs ? AssetSheetColumnMapScope.ALL_TABS : AssetSheetColumnMapScope.THIS_TAB,
		})
	}

	return (
		<DialogContent size="lg" isOpen onOpenChange={(open) => !open && onCancel()} isKeyboardDismissDisabled={saving}>
			<form onSubmit={submit} className="flex flex-col gap-ds-4">
				<div>
					<DialogTitle>Edit {column.columnName}</DialogTitle>
					<p className="mt-ds-1 text-ds-sm text-ds-text-muted">Tab {column.tab}. Changes apply from the next sync.</p>
				</div>

				<div className="grid gap-ds-4 sm:grid-cols-2">
					<FieldShell
						label="Excel column header"
						htmlFor="column-name"
						required
						error={errorFor(error, 'columnName')}
						description="As written in row 1 of the tab."
					>
						<Input
							id="column-name"
							size="sm"
							value={draft.columnName}
							maxLength={200}
							aria-describedby="column-name-description column-name-error"
							onChange={(e) => set({ columnName: e.target.value })}
						/>
					</FieldShell>
					<FieldShell label="Axis" htmlFor="column-axis" description="Fixed. Changing it is a seed change.">
						<Input
							id="column-axis"
							size="sm"
							value={column.axis}
							disabled
							readOnly
							aria-describedby="column-axis-description"
						/>
					</FieldShell>
				</div>

				{isAsset && <AssetFields draft={draft} set={set} fileTypes={fileTypes} error={error} />}

				<ReadingRules
					column={column}
					format={draft.format}
					onChange={(format) => set({ format })}
					parents={tabColumns.filter((c) => c.axis === column.axis && c.id !== column.id)}
					error={error}
				/>

				{sameColumnTabs > 1 && (
					<Checkbox isSelected={draft.allTabs} onChange={(allTabs) => set({ allTabs })}>
						Apply to {column.columnName} on every tab that maps it ({sameColumnTabs} tabs)
					</Checkbox>
				)}

				{error && !error.field?.match(/^(columnName|valueCode|level|cellFormat)/) && (
					<p role="alert" className="text-ds-sm text-ds-danger">
						{error.message}
					</p>
				)}

				<div className="flex justify-end gap-ds-2">
					<Button type="button" variant="secondary" isDisabled={saving} onPress={onCancel}>
						Cancel
					</Button>
					<Button type="submit" variant="primary" isDisabled={!canSave}>
						{saving ? 'Saving…' : 'Save'}
					</Button>
				</div>
			</form>
		</DialogContent>
	)
}

/** The help under a select, or the refusal that names it, as `FieldShell` shows them under an input. */
function FieldHelp({ error, help }: { error?: string; help: string }) {
	return error ? (
		<HelpText role="alert" variant="error" className="mt-1.5">
			{error}
		</HelpText>
	) : (
		<HelpText variant="hint" className="mt-1.5">
			{help}
		</HelpText>
	)
}

function AssetFields({
	draft,
	set,
	fileTypes,
	error,
}: {
	draft: Draft
	set: (patch: Partial<Draft>) => void
	fileTypes: SelectFieldOption[]
	error?: ColumnEditError | null
}) {
	return (
		<div className="grid gap-ds-4 sm:grid-cols-2">
			<div>
				<SelectField
					label="File type"
					value={draft.valueCode}
					options={fileTypes}
					isClearable={false}
					isRequired
					onChange={(valueCode) => set({ valueCode })}
				/>
				<FieldHelp error={errorFor(error, 'valueCode')} help="Every file in this column gets this file type." />
			</div>
			<div>
				<SelectField
					label="Tag files with"
					value={draft.level}
					options={LEVELS}
					isClearable={false}
					onChange={(level) => set({ level: isLevel(level) ? level : '' })}
				/>
				<FieldHelp
					error={errorFor(error, 'level')}
					help={draft.level ? LEVEL_HELP[draft.level] : 'Files get the file type only.'}
				/>
			</div>
		</div>
	)
}

const isLevel = (value: string): value is AssetSheetLevel =>
	(Object.values(AssetSheetLevel) as string[]).includes(value)

function ReadingRules({
	column,
	format,
	onChange,
	parents,
	error,
}: {
	column: AssetSheetColumnMapEntryDTO
	format: AssetSheetCellFormat
	onChange: (format: AssetSheetCellFormat) => void
	parents: AssetSheetColumnMapEntryDTO[]
	error?: ColumnEditError | null
}) {
	const isBrand = column.axis === AxisCode.BRAND
	const isProduct = isBrand && column.cellFormat.parentColumn !== undefined
	const setFormat = (patch: Partial<AssetSheetCellFormat>) => onChange({ ...format, ...patch })
	return (
		<fieldset className="flex flex-col gap-ds-3">
			<legend className={`mb-1.5 ${FIELD_LABEL_CLASS}`}>Reading rules</legend>
			<Switch isSelected={Boolean(format.required)} onChange={(required) => setFormat({ required })}>
				Required: an empty cell blocks the row
			</Switch>
			{isBrand && !isProduct && (
				<Switch isSelected={Boolean(format.rootsOnly)} onChange={(rootsOnly) => setFormat({ rootsOnly })}>
					Brands only: never match a product
				</Switch>
			)}
			{isProduct && (
				<div>
					<SelectField
						label="Product of"
						value={format.parentColumn ?? ''}
						options={parents.map((p) => ({ value: p.columnName, label: p.columnName }))}
						isClearable={false}
						onChange={(parentColumn) => setFormat({ parentColumn })}
					/>
					<FieldHelp
						error={errorFor(error, 'cellFormat.parentColumn')}
						help="Match only the products of this column's brand."
					/>
				</div>
			)}
			{!isBrand && column.axis !== AxisCode.GEOGRAPHY && (
				<>
					<TokenListField
						label="Split the cell on"
						tokens={format.split ?? []}
						onChange={(split) => setFormat({ split })}
						help="Each part is read on its own."
						error={errorFor(error, 'cellFormat.split')}
						keepWhitespace
						quickAdd={{ label: 'Add line break', token: '\n' }}
						emptyMessage="A separator cannot be empty. Type a character such as , or ;"
					/>
					<TokenListField
						label="Parts that mean nothing"
						tokens={format.ignore ?? []}
						onChange={(ignore) => setFormat({ ignore })}
						help="Skipped, compared without case, e.g. NA."
						error={errorFor(error, 'cellFormat.ignore')}
					/>
				</>
			)}
		</fieldset>
	)
}
